import apiClient from "./apiClient";

export type EntryReportReason =
  | "SPAM"
  | "PROFANITY"
  | "HATE_SPEECH"
  | "HARASSMENT"
  | "MISINFORMATION"
  | "PERSONAL_INFO"
  | "INAPPROPRIATE"
  // Entry-report-only reasons — deliberately not offered in the message report
  // modal (see MessageReportModal.tsx's own REPORT_REASONS list)
  | "AI_GENERATED"
  | "SPELLING_ERROR"
  | "NOT_A_DEFINITION"
  | "OTHER";

export type EntryReportStatus = "PENDING" | "DISMISSED" | "ACTIONED";

export interface SubmitReportRequest {
  reason: EntryReportReason;
  otherReason?: string;
}

export interface EntryReportDto {
  id: number;
  entryId: number;
  entryContent: string;
  entryAuthorId: number;
  entryAuthorUsername: string;
  // Match navigation — used to build the entry URL
  matchId?: number;
  homeTeam?: string;
  awayTeam?: string;
  matchDate?: string;
  // Club navigation
  clubSlug?: string;
  // Person navigation
  personSlug?: string;
  // Team navigation — URL: /takim/{teamClubSlug}/{teamSportSlug}
  teamClubSlug?: string;
  teamSportSlug?: string;
  // Custom topic (gündem) navigation — URL: /gundem/{topicSlug}
  topicSlug?: string;
  topicTitle?: string;
  reporterUserId: number;
  reporterUsername: string;
  reason: EntryReportReason;
  otherReason?: string;
  createdAt: string;
  status: EntryReportStatus;
  resolvedById?: number;
  resolvedByUsername?: string;
  resolvedAt?: string;
  resolutionNote?: string;
}

/**
 * Submit a new report for the given entry.
 * Throws on 409 ALREADY_REPORTED or 400 validation errors.
 */
export async function submitEntryReport(
  entryId: number,
  data: SubmitReportRequest
): Promise<void> {
  await apiClient.post(`/api/entries/${entryId}/report`, data);
}

/**
 * Check whether the authenticated user has already reported this entry.
 * Used to disable the report button without requiring a failed submit.
 * apiClient.get returns T directly — no .data wrapper.
 */
export async function getReportStatus(
  entryId: number
): Promise<{ reported: boolean }> {
  return apiClient.get<{ reported: boolean }>(
    `/api/entries/${entryId}/report/status`
  );
}

// ── Admin endpoints ───────────────────────────────────────────────────────

export interface AdminReportFilters {
  status: string;
  authorUsername: string;
  reporterUsername: string;
  reason: string;
  startDate: string;
  endDate: string;
}

export interface AdminReportsPage {
  content: EntryReportDto[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

export async function getAdminReports(
  filters: AdminReportFilters,
  page: number,
  size: number
): Promise<AdminReportsPage> {
  // apiClient.get takes only the endpoint string — build query params manually
  const params = new URLSearchParams({
    status:           filters.status,
    authorUsername:   filters.authorUsername,
    reporterUsername: filters.reporterUsername,
    reason:           filters.reason,
    startDate:        filters.startDate,
    endDate:          filters.endDate,
    page:             String(page),
    size:             String(size),
  });
  return apiClient.get<AdminReportsPage>(`/api/admin/reports?${params}`);
}

export async function dismissReport(
  reportId: number,
  note?: string
): Promise<EntryReportDto> {
  return apiClient.patch<EntryReportDto>(
    `/api/admin/reports/${reportId}/dismiss`,
    { note }
  );
}

export async function actionReport(
  reportId: number,
  note?: string
): Promise<EntryReportDto> {
  return apiClient.patch<EntryReportDto>(
    `/api/admin/reports/${reportId}/action`,
    { note }
  );
}